import { Graphics, Text } from 'pixi.js';
import type { GameConfig } from '../app/GameConfig';
import type { ViewportLayout } from '../app/Viewport';
import { listCases } from '../app/ScenarioCatalog';
import { BaseScene, type SceneServices } from './BaseScene';
import { Button, Panel, UI_COLORS, UI_FONT_FAMILY } from '../ui';

export interface CreditsSceneOptions {
  onBack?: () => void;
  onGoToTitle?: () => void;
}

const PANEL_WIDTH = 900;
const PANEL_HEIGHT = 520;

export class CreditsScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly titleText = new Text({
    text: 'クレジット',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly subtitleText = new Text({
    text: '制作情報',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });
  private readonly panel = new Panel({
    width: PANEL_WIDTH,
    height: PANEL_HEIGHT,
    title: '作品情報'
  });
  private readonly creditText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      lineHeight: 34,
      wordWrap: true,
      wordWrapWidth: PANEL_WIDTH - 96
    }
  });
  private readonly backButton = new Button({
    label: '戻る',
    width: 150,
    height: 48,
    variant: 'ghost',
    onPress: () => this.options.onBack?.()
  });
  private readonly titleButton = new Button({
    label: 'タイトルへ',
    width: 180,
    height: 48,
    variant: 'secondary',
    onPress: () => this.options.onGoToTitle?.()
  });

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: CreditsSceneOptions = {}
  ) {
    super(services);
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.container.addChild(this.titleText);
    this.container.addChild(this.subtitleText);
    this.container.addChild(this.panel);
    this.container.addChild(this.backButton);
    this.container.addChild(this.titleButton);
    this.panel.content.addChild(this.creditText);
    this.creditText.text = createCreditText(this.config);
    this.resize(this.services.getLayout());
  }

  public override resize(layout: ViewportLayout): void {
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.titleText.position.set(layout.safeArea.x, layout.safeArea.y + 16);
    this.subtitleText.position.set(layout.safeArea.x + 4, layout.safeArea.y + 60);
    this.panel.position.set(Math.round((layout.baseWidth - PANEL_WIDTH) / 2), 128);
    this.backButton.position.set(
      layout.safeArea.x,
      layout.safeArea.y + layout.safeArea.height - 58
    );
    this.titleButton.position.set(
      layout.safeArea.x + layout.safeArea.width - 180,
      layout.safeArea.y + layout.safeArea.height - 58
    );
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.background.rect(0, 0, width, height).fill({ color: UI_COLORS.redDark, alpha: 0.2 });
    this.background.rect(0, 0, width, 8).fill({ color: UI_COLORS.red, alpha: 0.84 });
    this.background.rect(0, height - 8, width, 8).fill({ color: UI_COLORS.gold, alpha: 0.72 });
  }
}

function createCreditText(config: GameConfig): string {
  return [
    config.projectFullTitle,
    '',
    '企画・開発: nun-game',
    'ジャンル: ギャグミステリーADV / 不条理ノベル',
    '使用技術: Vite / TypeScript / PixiJS / Howler.js',
    '',
    '本作は、独自の登場人物・舞台・物語で制作するオリジナル作品です。',
    '既存の漫画、アニメ、ゲーム、実在の人物・団体とは関係ありません。',
    '',
    `収録シナリオ: ${createScenarioRangeLabel()}`
  ].join('\n');
}

function createScenarioRangeLabel(): string {
  const caseOrders = listCases().map((caseDefinition) => caseDefinition.displayOrder);
  const firstCaseOrder = Math.min(...caseOrders);
  const lastCaseOrder = Math.max(...caseOrders);

  if (!Number.isFinite(firstCaseOrder) || !Number.isFinite(lastCaseOrder)) {
    return '未登録';
  }

  if (firstCaseOrder === lastCaseOrder) {
    return `Case ${firstCaseOrder}`;
  }

  return `Case ${firstCaseOrder} - Case ${lastCaseOrder}`;
}
